<template>
    <app-layout title="Dashboard">
        <cart-error-modal 
            :show="showCartError" 
            @close="hideCartError"
        />
        <div class="lg:flex">
            <sidebar-catalogue :categories="categories"/>

            <div class="flex-1 catalogue">
                <div v-if="isSchoolCustomer" class="xl:hidden px-2 pt-1 sm:pt-2 pb-0 lg:px-8 sm:font-semibold text-gray-500 text-xs text-center">
                    <div class="sm:hidden">Order deadline is Wed. @ 11:59pm for delivery the next week</div>
                    <div class="hidden sm:block sm:text-sm">Order deadline is Wednesday at 11:59pm for delivery the following week</div>
                </div>

                <div class="border-b border-gray bg-white sticky top-0 z-10 p-2 lg:px-8 ">
                    <div class="md:flex justify-between items-center">
                        <form @submit.prevent="handleSearch" class="md:flex gap-x-4 w-full" style="max-width: 800px">
                            <div class="button-group flex-1">
                                <select v-model="searchForm.category" @change="handleSearch" class="rounded-r-none">
                                    <option value="">All Categories</option>
                                    <option v-if="activeCustomer || isAdmin" value="favourites">Favourites</option>
                                    <template v-for="parentCategory in groupedCategories" :key="parentCategory.id">
                                        <optgroup :label="parentCategory.title" :value="parentCategory.slug">
                                            <option :value="parentCategory.slug">All {{ parentCategory.title }}</option>
                                            <option 
                                                v-for="subcategory in parentCategory.subcategories" 
                                                :key="subcategory.id"
                                                :value="subcategory.slug"
                                            >
                                                {{ subcategory.title }}
                                            </option>
                                        </optgroup>
                                    </template>
                                </select>

                                <input
                                    v-model.lazy="searchForm.search"
                                    type="text"
                                    class="w-full rounded-r-md"
                                    placeholder="Search..."
                                    ref="searchForm"
                                    v-input_delay="500"
                                    @change="handleSearch"
                                />
                            </div>

                            <div class="flex justify-between gap-x-4 items-center mt-2 md:my-0">
                                <div class="flex items-center gap-x-2">
                                    <toggle v-model="searchForm.local" />
                                    <jet-label for="local" value="Local Only" class="mb-0" />
                                </div>

                                <div class="xl:hidden">
                                    <cart-widget-mobile v-if="showCartWidget"/>
                                </div>
                            </div>
                        </form>

                        <div v-if="isSchoolCustomer" class="hidden xl:block font-semibold text-gray-500 text-xs text-center" style="max-width:275px">
                            Order Deadline is Wednesday at 11:59pm for delivery the following week
                        </div>

                        <div class="hidden xl:block">
                            <cart-widget v-if="showCartWidget"/>
                        </div>
                    </div>
                    
                    <ul v-if="cartChanges && cartChanges.length && !dismissedCartChanges" class="mt-4">
                        <li v-for="(change, index) in cartChanges" :key="index" class="bg-blue-100 border rounded-md border-blue-300 p-2 my-1 relative">
                            <a href="#" @click.prevent="dismissCartChanges" class="absolute top-1 right-1"><i class="fas fa-times-circle"></i></a>

                            {{ change.message}}

                            <ul v-if="change.products && change.products.length" class="list-disc ml-10">
                                <li v-for="(product, index) in change.products" :key="'product' + index">{{ product }}</li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <div class="py-4 px-3 lg:px-5">
                    <p v-if="isAdmin && !activeCustomer && isFavouritesRoute" class="text-center text-gray-600 mb-4">
                        Select a customer from the dropdown in the sidebar to view their favourites.
                    </p>
                    <div v-for="categoryData in productsByCategory" :key="categoryData.category.id">
                        <h2 class="category-title">
                            {{ categoryData.category.title }}
                            <span v-if="isSubcategory"> - {{ currentCategory.title }}</span>
                        </h2>
                        <div class="flex flex-wrap -mx-3">
                            <div v-for="product in categoryData.products" :key="product.id" class="w-full xs:w-1/2 md:w-1/4 xl:w-1/6 px-3 mb-6">
                                <product-summary
                                    :product="product"
                                    :favourite-product-ids="effectiveFavouriteProductIds"
                                    :favourite-details="effectiveFavouriteDetails"
                                    :show-favourite-star="!!activeCustomer"
                                    :customer-favourites-visible-only="!!customerFavouritesVisibleOnly"
                                    @favourites-updated="onFavouritesUpdated"
                                />
                            </div>

                            <p v-if="categoryData.products.length === 0" class="text-center text-xl w-full">
                                No products at this time.
                            </p>
                        </div>
                    </div>

                    <div v-if="productsByCategory.length === 0">
                        <h2 v-if="currentCategory" class="category-title">
                            <div v-if="isSubcategory">
                                {{ currentParentCategory.title }} - {{ currentCategory.title }}
                            </div>
                            <div v-else>
                                {{ currentCategory.title }}
                            </div>
                        </h2>

                        <p class="text-center text-xl w-full">
                            No products at this time.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script>
import { onMounted } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import UseCustomerAccess from '@/Composables/UseCustomerAccess';
import AppLayout from '@/Layouts/AppLayout.vue'
import SidebarCatalogue from '@/Layouts/SidebarCatalogue.vue';
import CartWidget from '@/Pages/Catalogue/Partials/CartWidget.vue';
import CartWidgetMobile from '@/Pages/Catalogue/Partials/CartWidgetMobile.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetLabel from '@/Jetstream/Label';
import ProductSummary from '@/Pages/Catalogue/Partials/ProductSummary';
import JetButton from '@/Jetstream/Button';
import UseCart from '@/Composables/UseCart';
import CartErrorModal from '@/Components/CartErrorModal';
import Toggle from '@vueform/toggle';

import '@vueform/toggle/themes/default.css';

export default {
    setup () {
        const { isCustomer, isAdmin } = UseUserAuth(usePage().props.value.user);
        const { canPlaceOrders } = UseCustomerAccess();
        const { fetchCart, showCartError, hideCartError } = 
            UseCart(
                usePage().props.value.user,
                usePage().props.value.activeCustomer ? usePage().props.value.activeCustomer.tax_exempt : 0
            );

        const shouldFetchCart = () => isAdmin.value || canPlaceOrders.value;

        if (shouldFetchCart()) {
            onMounted(fetchCart);
        }

        return {
            isCustomer,
            isAdmin,
            canPlaceOrders,
            fetchCart,
            shouldFetchCart,
            showCartError,
            hideCartError,
        }
    },

    components: {
        AppLayout,
        CartErrorModal,
        SidebarCatalogue,
        CartWidget,
        CartWidgetMobile,
        JetInput,
        JetLabel,
        ProductSummary,
        JetButton,
        Toggle,
    },

    props: ['categories', 'slug', 'products', 'user', 'cartChanges', 'favourites', 'favouriteProductIds', 'favouriteDetails', 'customerFavouritesVisibleOnly'],

    data() {
        return {
            searchForm: this.$inertia.form({
                search: this.route().params.search,
                category: this.initialCategoryValue(),
                local: this.route().params.local === 'true' ? true : '',
            }),
            
            showStaticCategories: this.slug === null || this.route().current() === 'catalogue.hot_buys', 
            showDynamicCategories: this.route().current() !== 'catalogue.hot_buys',

            dismissedCartChanges: false,
            localFavouriteProductIds: null,
            localFavouriteDetails: null,
        }
    },

    methods: {
        initialCategoryValue() {
            if (this.route().current() === 'catalogue.favourites') {
                return 'favourites';
            }

            return this.slug ?? this.route().params.category ?? '';
        },

        handleSearch () {
            if (this.searchForm.category === 'favourites') {
                this.searchForm.get(this.route('catalogue.favourites'));
                return;
            }

            this.searchForm.get(this.route('catalogue', this.searchForm.category !== null ? this.searchForm.category : ''))
        },

        handleFilterLocal () {
            this.searchForm.get(this.route('catalogue', this.slug !== null ? this.slug : ''))
        },

        dismissCartChanges () {
            this.dismissedCartChanges = true;
        },

        onFavouritesUpdated (payload) {
            const ids = payload && payload.favouriteProductIds ? payload.favouriteProductIds : payload;
            const details = payload && payload.favouriteDetails ? payload.favouriteDetails : null;
            this.localFavouriteProductIds = ids;
            if (details !== null) this.localFavouriteDetails = details;
        },
    },

    computed: {
        activeCustomer() {
            return this.$page.props.activeCustomer;
        },

        isSchoolCustomer() {
            return this.activeCustomer?.type === 'School';
        },

        showCartWidget() {
            return this.activeCustomer && (this.canPlaceOrders || this.isAdmin);
        },

        currentCategory() {
            return this.allCategories.find(c => c.slug === this.slug);
        },

        currentParentCategory() {
            let currentParentCategory = null;

            if (this.isSubcategory)
            {
                currentParentCategory = this.allCategories.find(c => c.id === this.currentCategory.parent_id);
            }

            return currentParentCategory;
        },

        isSubcategory() {
            let isSubcategory = false;

            if (this.currentCategory !== undefined && this.currentCategory.parent_id !== null)
            {
                isSubcategory = true;
            }

            return isSubcategory;
        },

        effectiveFavouriteProductIds() {
            return this.localFavouriteProductIds !== null
                ? this.localFavouriteProductIds
                : (this.favouriteProductIds || []);
        },

        effectiveFavouriteDetails() {
            return this.localFavouriteDetails !== null
                ? this.localFavouriteDetails
                : (this.favouriteDetails || {});
        },

        isFavouritesRoute() {
            return this.route().current() === 'catalogue.favourites';
        },

        staticCategories() {
            const categories = [
                { id: null, parent_id: null, order_index: null, slug: 'hot_buys', title: 'Featured Products', subcategories: []},
            ];

            if (!this.isFavouritesRoute) {
                categories.push({ id: null, parent_id: null, order_index: null, slug: 'new', title: 'New', subcategories: []});
            }

            // Featured Products does not load the favourites list, so this section would be an empty header there.
            if (
                this.route().current() !== 'catalogue.hot_buys'
                && this.effectiveFavouriteProductIds
                && this.effectiveFavouriteProductIds.length > 0
            ) {
                categories.push({
                    id: null,
                    parent_id: null,
                    order_index: null,
                    slug: 'favourites',
                    title: 'Favourites',
                    subcategories: [],
                });
            }

            return categories;
        },

        allCategories() {
            let staticCategories = (this.showStaticCategories ? this.staticCategories : []);
            let dynamicCategories = (this.showDynamicCategories ? this.categories : []);

            return staticCategories.concat(dynamicCategories);
        },

        availableCategories () {
            let availableCategories = [];

            this.allCategories.forEach(c => {
                const match = (Object.values(this.products)).find(p => {
                    if (c.slug === 'hot_buys') {
                        return p.hot_buy === true;
                    }
                    if (c.slug === 'new') {
                        return p.new === true;
                    }
                    if (c.slug === 'favourites') {
                        return this.effectiveFavouriteProductIds && this.effectiveFavouriteProductIds.length > 0;
                    }

                    if (c.parent_id !== null) return false;

                    const submatch = c.subcategories.find(sub => p.category_id === sub.id);

                    return (submatch || p.category_id === c.id) && p.enabled === true;
                });

                if (match) {
                    availableCategories.push(c);
                }
            });

            return availableCategories;
        },

        groupedCategories() {
            // Group categories by parent-child relationship
            const parentCategories = this.categories.filter(c => c.parent_id === null);
            const subcategories = this.categories.filter(c => c.parent_id !== null);
            
            return parentCategories.map(parent => ({
                ...parent,
                subcategories: subcategories.filter(sub => sub.parent_id === parent.id)
            }));
        },

        productsByCategory() {
            let productsByCategory = [];

            this.availableCategories.forEach(c => {
                let categoryProducts;
                
                if (c.slug === 'favourites') {
                    categoryProducts = (this.favourites && this.favourites.length) ? this.favourites : [];
                } else {
                    categoryProducts = (Object.values(this.products)).filter(p => {
                    if (c.slug === 'hot_buys') {
                        return p.hot_buy === true;
                    }
                    if (c.slug === 'new') {
                        return p.new === true;
                    }
                    const submatch = c.subcategories.find(sub => p.category_id === sub.id);
                    return (submatch || p.category_id === c.id) && p.enabled === true;
                    });
                }

                productsByCategory.push({
                    category: c,
                    products: categoryProducts
                });
            });

            return productsByCategory;
        }
    },

    watch: {
        '$page.url'() {
            this.searchForm.category = this.initialCategoryValue();
        },
        'searchForm.local': {
            deep: true,
            handler: function() {
                this.handleFilterLocal();
            }
        },
        activeCustomer: {
            handler() {
                if (this.shouldFetchCart()) {
                    this.fetchCart();
                }
            },
            deep: true
        },
        cartChanges: {
            handler(newVal, oldVal) {
                // Reset dismissed state when new cart changes come in
                if (newVal && newVal.length > 0 && (!oldVal || oldVal.length === 0)) {
                    this.dismissedCartChanges = false;
                }
            },
            deep: true
        },
    },

    mounted () {
        if (this.route().params.search) {
            this.$refs.searchForm.focus();
        }
    }
}
</script>

<style scoped>
.button-group button {
    min-width: 34px;
}

@media (min-width: 1024px) {
    .catalogue {
        padding-left: 240px;
    }
}

.category-title {
    text-align: center;
    line-height: 1.5;
    margin-bottom: 1.5rem;
    border-bottom: 2px solid #85C71E;
}

@media (min-width: 768px) {
    .category-title {
        font-size: 2.375rem;
        text-align: left;
    }
}
</style>